// src/components/ordering/ItemCustomizeModal.jsx
import React, { useMemo, useState } from "react";
import { getCurrencySymbol } from "../libs/countryData";

export default function ItemCustomizeModal({ item, onClose, onConfirm }) {
  const [qty, setQty] = useState(1);
  // state per group: { [groupName]: Set(optionName) }
  const [selected, setSelected] = useState(() => {
    const init = {};
    (item.modifierGroups || []).forEach((g) => {
      init[g.groupName] = [];
    });
    return init;
  });

  const basePrice = Number(item.price || 0);

  function toggleOption(group, option) {
    setSelected((prev) => {
      const cur = prev[group.groupName] ? [...prev[group.groupName]] : [];
      const idx = cur.findIndex((c) => c.name === option.name);
      if (idx !== -1) {
        // remove
        cur.splice(idx, 1);
      } else {
        // add, but respect maxChoices
        if (
          typeof group.maxChoices === "number" &&
          group.maxChoices > 0 &&
          cur.length >= group.maxChoices
        ) {
          // replace last picked
          cur.pop();
        }
        cur.push({ name: option.name, priceDelta: option.priceDelta || 0 });
      }
      return {
        ...prev,
        [group.groupName]: cur,
      };
    });
  }

  // compute price
  const totalPerUnit = useMemo(() => {
    const modsTotal = Object.entries(selected).reduce((sum, [gName, arr]) => {
      return (
        sum +
        (arr || []).reduce(
          (s, o) => s + Number(o.priceDelta || 0),
          0
        )
      );
    }, 0);
    return basePrice + modsTotal;
  }, [basePrice, selected]);

  function handleConfirm() {
    // validate required groups
    for (const g of item.modifierGroups || []) {
      const picked = selected[g.groupName] || [];
      if ((g.minChoices || 0) > 0 && picked.length < g.minChoices) {
        alert(`Please pick at least ${g.minChoices} for "${g.groupName}"`);
        return;
      }
    }

    const chosenModifiers = (item.modifierGroups || []).map((g) => ({
      groupName: g.groupName,
      selections: (selected[g.groupName] || []).map((s) => ({
        name: s.name,
        priceDelta: Number(s.priceDelta || 0),
      })),
    }));

    onConfirm({
      itemId: item._id,
      name: item.name,
      basePrice: basePrice,
      chosenModifiers,
      qty: qty,
    });
  }

  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,0.4)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header py-2">
            <h6 className="modal-title">{item.name}</h6>
            <button className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {item.description ? (
              <div style={{ fontSize: "0.7rem", marginBottom: "0.5rem" }}>
                {item.description}
              </div>
            ) : null}

            {(item.modifierGroups || []).map((g, idx) => (
              <div key={idx} className="mb-3">
                <div style={{ fontWeight: 500, fontSize: "0.8rem" }}>
                  {g.groupName}{" "}
                  {g.minChoices > 0 && (
                    <span style={{ color: "#d00", fontSize: "0.65rem" }}>
                      (pick at least {g.minChoices})
                    </span>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {(g.options || []).map((op, i2) => {
                    const picked = (selected[g.groupName] || []).some(
                      (x) => x.name === op.name
                    );
                    return (
                      <button
                        key={i2}
                        type="button"
                        className={`btn btn-sm ${
                          picked ? "btn-primary" : "btn-outline-secondary"
                        }`}
                        onClick={() => toggleOption(g, op)}
                      >
                        {op.name}
                        {Number(op.priceDelta || 0) > 0
                          ? ` (+${getCurrencySymbol()}${Number(op.priceDelta).toFixed(0)})`
                          : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* qty + summary */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="input-group input-group-sm" style={{ width: "120px" }}>
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setQty((prev) => Math.max(1, prev - 1))}
                >
                  -
                </button>
                <input
                  type="number"
                  className="form-control"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setQty((prev) => prev + 1)}
                >
                  +
                </button>
              </div>
              <div style={{ fontWeight: 600 }}>
                {getCurrencySymbol()}{(totalPerUnit * qty).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="modal-footer py-2">
            <button className="btn btn-sm btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-sm btn-success" onClick={handleConfirm}>
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
